import type { IntlShape } from 'react-intl';

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** "Oct 24": the short date of the Outreach pages. */
export function shortDate(intl: IntlShape, iso: string | null): string {
  if (iso === null || iso === '') {
    return '—';
  }
  return intl.formatDate(iso, { month: 'short', day: 'numeric' });
}

/** "5m", "2h", "3d", then the short date: the time column of the feed and the inbox (frames a1, g1). */
export function shortAgo(intl: IntlShape, iso: string, now: number = Date.now()): string {
  const diff = Math.max(0, now - Date.parse(iso));
  if (diff < HOUR) {
    return intl.formatNumber(Math.max(1, Math.round(diff / MINUTE)), {
      style: 'unit',
      unit: 'minute',
      unitDisplay: 'narrow',
    });
  }
  if (diff < DAY) {
    return intl.formatNumber(Math.round(diff / HOUR), { style: 'unit', unit: 'hour', unitDisplay: 'narrow' });
  }
  if (diff < 7 * DAY) {
    return intl.formatNumber(Math.round(diff / DAY), { style: 'unit', unit: 'day', unitDisplay: 'narrow' });
  }
  return shortDate(intl, iso);
}

/** A whole percent of `part` in `total`; 0 for no total. */
export function percentOf(part: number, total: number): number {
  return total <= 0 ? 0 : Math.round((part / total) * 100);
}

/** "2:30": whole seconds as M:SS (the listen times, frames s1, s2). Null or negative shows a dash. */
export function secondsToClock(seconds: number | null): string {
  if (seconds === null || seconds < 0) {
    return '—';
  }
  const whole = Math.round(seconds);
  const minutes = Math.floor(whole / 60);
  const rest = whole % 60;
  return `${minutes}:${rest.toString().padStart(2, '0')}`;
}

/** "18,240" or "1.2M": a follower count. Null shows a dash. */
export function compactCount(intl: IntlShape, value: number | null): string {
  if (value === null) {
    return '—';
  }
  return value >= 100000
    ? intl.formatNumber(value, { notation: 'compact', maximumFractionDigits: 1 })
    : intl.formatNumber(value);
}

/** Two letters for an avatar: "Lena Vogt" → "LV", "Quiet Hours" → "QH", "tidepool" → "TI". */
export function nameInitials(name: string): string {
  const words = name
    .split(/[\s._@-]+/)
    .map((word) => word.replace(/[^\p{L}\p{N}]/gu, ''))
    .filter((word) => word !== '');
  const [first, second] = words;
  if (first !== undefined && second !== undefined) {
    return `${first.charAt(0)}${second.charAt(0)}`.toUpperCase();
  }
  return (first ?? '?').slice(0, 2).toUpperCase();
}

/**
 * "Lena · Quiet Folk Mornings": who answered and their outlet (frame g1). A contact with no own
 * name carries the outlet name, so the outlet shows once, not twice.
 */
export function replyTitle(contactName: string, targetName: string | null): string {
  if (targetName === null || targetName === '' || targetName === contactName) {
    return contactName;
  }
  return `${contactName} · ${targetName}`;
}
